/**
 * Shared recording-field validators — the single source of truth for the
 * business rules enforced when editing recording-level fields.
 *
 * The Recording Edit Form is the gold standard; these primitives were extracted
 * from it so that the mass-edit and bulk-edit surfaces validate identically and
 * the rules cannot drift per surface. Keep this module pure (no React / JSX
 * imports) so every surface can import it freely.
 *
 * Note: `validateReleaseYear` lives in ./Components/ChangeReleaseModal (it
 * delegates to `validateYearInput` below so release and recording years share
 * one set of bounds) and `validateEvidenceUrl` in
 * ./Components/ContributionDetailModal — both are already exported/shared from
 * their component homes, so they are intentionally not duplicated here.
 */

// ── Four-digit years (Year of Release, Year of Recording) ────────────────────

export const MIN_YEAR = 1920;
/** Upper bound is `current year + MAX_YEAR_OFFSET`. */
export const MAX_YEAR_OFFSET = 2;

/**
 * Validate an optional four-digit year against the shared bounds
 * (1920 ≤ year ≤ current year + 2). Returns the error message or null. Empty
 * is valid — both year fields are optional.
 */
export const validateYearInput = (value: string): string | null => {
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (!/^\d{4}$/.test(trimmed)) return 'Year must be a 4-digit number';
  const year = Number(trimmed);
  const maxYear = new Date().getFullYear() + MAX_YEAR_OFFSET;
  if (year < MIN_YEAR) return `Year cannot be before ${MIN_YEAR}`;
  if (year > maxYear) return `Year cannot be after ${maxYear}`;
  return null;
};

/** Year of Recording: same shape and bounds as the release year. */
export const validateRecordingYear = (
  value: string | null | undefined
): string | null => validateYearInput(value ?? '');

// ── Recording-before-release rule ────────────────────────────────────────────

/**
 * Extract the year from a stored date value the same way the backend does:
 * a bare `YYYY` string is taken as is, anything else is parsed as a date and
 * its year taken. Returns null when the value is absent or unparseable, which
 * every caller treats as "skip the check".
 */
export const extractYear = (
  value: string | null | undefined
): number | null => {
  const trimmed = typeof value === 'string' ? value.trim() : '';
  if (!trimmed) return null;
  if (/^\d{4}$/.test(trimmed)) return Number(trimmed);
  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.getFullYear();
};

/** Which side of the rule the user just changed — picks the message wording. */
export type RecordingBeforeReleaseSide = 'recording' | 'release';

/**
 * The recording-before-release rule: `year(yearRecorded) ≤ year(dateReleased)`.
 *
 * `dateReleased` may be `YYYY` or a full date string (the earliest linked
 * release's date). Skipped — returns undefined — when either side is missing
 * or unparseable, so a skipped check never blocks a save. The message names
 * the side that changed (`changed`, default 'recording'), matching the
 * backend's wording for the same rejection.
 */
export const validateRecordingBeforeRelease = (
  yearRecorded: string | null | undefined,
  dateReleased: string | null | undefined,
  changed: RecordingBeforeReleaseSide = 'recording'
): string | undefined => {
  const recorded = extractYear(yearRecorded);
  const released = extractYear(dateReleased);
  if (recorded === null || released === null) return undefined;
  if (recorded <= released) return undefined;
  return changed === 'release'
    ? `Release year (${released}) cannot be earlier than the recording's Year of Recording (${recorded}).`
    : `Year of Recording (${recorded}) cannot be later than the release year (${released}) of the linked release.`;
};

/** Convenience for callers that changed the release side. */
export const validateReleaseAfterRecording = (
  dateReleased: string | null | undefined,
  yearRecorded: string | null | undefined
): string | undefined =>
  validateRecordingBeforeRelease(yearRecorded, dateReleased, 'release');

// ── Live descriptor ──────────────────────────────────────────────────────────

/** Normalises a `live` value to a trimmed string. The backend field is
 *  migrating from boolean to string, so any non-string (legacy boolean) is
 *  treated as "no live descriptor" rather than crashing on `.trim()`. */
export const liveToString = (v: unknown): string =>
  typeof v === 'string' ? v.trim() : '';

// ── ISRC format ──────────────────────────────────────────────────────────────

/** ISRC: 2 letter country code, 3 char registrant code, 2 digit year, 5 digit
 *  designation code (12 chars total, e.g. GBAAA0912345). */
export const ISRC_REGEX = /^[A-Z]{2}[A-Z0-9]{3}\d{2}\d{5}$/;

/** True if `value` is a well-formed ISRC. Normalises case and strips dashes /
 *  spaces for the check only — callers store the raw trimmed value. */
export const isValidIsrcFormat = (value: string): boolean =>
  ISRC_REGEX.test(value.toUpperCase().replace(/[-\s]/g, ''));

export const ISRC_FORMAT_ERROR =
  'Invalid ISRC format. Expected: 2 letter country code, 3 char registrant code, 2 digit year, 5 digit designation (e.g. GBAAA0912345)';

// ── Duration (write-once, HH:MM:SS) ──────────────────────────────────────────

export const DURATION_REGEX = /^\d{2}:\d{2}:\d{2}$/;

export const DURATION_FORMAT_HINT = 'Format: HH:MM:SS (e.g. 00:03:45)';

/** Minutes and seconds are clock parts — 60 of either rolls into the next unit,
 *  so anything above 59 is a typo (03:64:12 should be 04:04:12). Hours are
 *  deliberately unbounded; a long-form recording can exceed 24h. */
const isDurationInRange = (trimmed: string): boolean => {
  const [, minutes, seconds] = trimmed.split(':').map(Number);
  return minutes <= 59 && seconds <= 59;
};

/** Valid duration is empty (field optional until filled) or HH:MM:SS with
 *  minutes and seconds in 00–59. The value is trimmed before the check. */
export const isValidDuration = (value: string): boolean => {
  const trimmed = value.trim();
  if (!trimmed) return true;
  return DURATION_REGEX.test(trimmed) && isDurationInRange(trimmed);
};

/**
 * Explain *why* a duration is rejected, rather than restating the format.
 *
 * Rejects exactly what `isValidDuration` rejects — this only chooses the
 * wording, so the two can't disagree about what's valid. Returns null when the
 * value is fine (including empty, since duration is optional).
 */
export const describeDurationError = (value: string): string | null => {
  const trimmed = value.trim();
  if (!trimmed) return null;
  // Well-formed: the only thing left to check is the minute/second range.
  if (DURATION_REGEX.test(trimmed)) {
    if (isDurationInRange(trimmed)) return null;
    const [, minutes, seconds] = trimmed.split(':').map(Number);
    const over =
      minutes > 59 && seconds > 59
        ? 'Minutes and seconds'
        : minutes > 59
        ? 'Minutes'
        : 'Seconds';
    return `${over} must be 00–59 — 03:64:12 means 4 minutes past the hour, so write it as 04:04:12.`;
  }
  if (/[^\d:]/.test(trimmed)) {
    return 'Use digits and colons only — no letters, spaces or other characters.';
  }
  const parts = trimmed.split(':');
  if (parts.length < 3) {
    return 'Needs all three parts — hours, minutes and seconds, e.g. 00:03:45 for 3 minutes 45 seconds.';
  }
  if (parts.length > 3) {
    return 'Too many parts — use hours, minutes and seconds only, e.g. 00:03:45.';
  }
  if (parts.some((p) => p.length !== 2)) {
    return 'Each part needs exactly two digits — write 3:45 as 00:03:45.';
  }
  // Three two-digit parts of digits-and-colons always match DURATION_REGEX, so
  // this is unreachable in practice — kept as a safety net for the format hint.
  return DURATION_FORMAT_HINT;
};

// ── Recording title ──────────────────────────────────────────────────────────

/** Title is required; emptiness is tested on the trimmed value only. */
export const isTitleValid = (value: string): boolean => value.trim().length > 0;

// ── Write-once / manual-change editability gate ──────────────────────────────

/**
 * A write-once, system-populated field (Duration, the Live descriptor) may only
 * be edited when it is currently **unset**, or when it was **manually changed
 * before** (it carries a manual-change marker from the manual-changes overlay).
 * A value that is already set and has never been manually edited is locked.
 *
 * This is the gold-standard rule the Recording Edit Form applies to Duration;
 * it is shared here so the edit form, mass-edit and bulk-edit all gate Duration
 * and the Live descriptor identically.
 */
export const isWriteOnceFieldEditable = (
  currentValue: string | null | undefined,
  wasManuallyEdited: boolean
): boolean => {
  const isSet = !!(currentValue && currentValue.toString().trim());
  return !isSet || wasManuallyEdited;
};

// ── Recording versions ───────────────────────────────────────────────────────

export const VERSION_EMPTY_ERROR =
  'A version can be renamed but not removed — give every version a name, or cancel to leave it unchanged.';

/**
 * Versions are edit-only and positional: once a recording has a version, that
 * version can be *renamed* but never removed and never blanked out. Every
 * editing surface funnels through this so the rule can't drift per surface.
 *
 * Returns null when `versions` is a legal edit of a list of the same length,
 * else the message to show the user.
 */
export const describeVersionsError = (versions: string[]): string | null => {
  const trimmed = versions.map((v) => v.trim());
  if (trimmed.some((v) => v.length === 0)) {
    return VERSION_EMPTY_ERROR;
  }
  const duplicate = findDuplicate(trimmed);
  if (duplicate) {
    return `“${duplicate}” is listed more than once. Versions must be unique.`;
  }
  return null;
};

// ── Case-insensitive duplicate detection (versions, ISRCs, labels, …) ─────────

/** Returns the first case-insensitive duplicate in `values`, or null. */
export function findDuplicate(values: string[]): string | null {
  const seen = new Set<string>();
  for (const v of values) {
    const key = v.toLowerCase();
    if (seen.has(key)) return v;
    seen.add(key);
  }
  return null;
}

// ── Contribution alias (persona) ─────────────────────────────────────────────

/**
 * A contribution cannot be persisted without an alias.
 *
 * The backend writes the `contributionWestbury` edge with `source_id` set to
 * the persona id, so an edit sent without one does not 400 — it writes an edge
 * with an empty source. That makes this a *frontend* obligation on every
 * surface that adds or amends a contribution: the recording edit form, mass
 * edit and bulk edit alike.
 *
 * Note this holds for `edit` as much as for `add`. The two used to differ (an
 * edit targets a contribution that already exists, so the alias looked
 * redundant) but the edge write is the same either way.
 */
export const hasContributionAlias = (
  personaId: string | null | undefined
): boolean => !!personaId && personaId.trim().length > 0;

export const CONTRIBUTION_ALIAS_REQUIRED_ERROR =
  'Select an alias — a contribution cannot be saved without one.';

/** The same message naming the role, for the surfaces that report problems in
 *  a list rather than against the field (bulk edit, mass edit). */
export const contributionAliasProblem = (role: string): string =>
  `Contribution “${role}” has no alias selected — reopen it and choose one.`;

/**
 * Resolve the alias a recording's contributions belong to, for seeding the
 * picker when reopening an existing contribution.
 *
 * The contribution read path does not carry the persona id (the Gremlin
 * projection takes the edge's `valueMap()`, which has no source vertex, and the
 * serializer never added one), so a server-sourced contribution always arrives
 * with a blank alias. What a recording *does* carry is `persona.stageName` —
 * the alias credited on it — which matches by name against the client's alias
 * list from `lookup_client_aliases`.
 *
 * Lossy by nature: ingest folds the contributing personas and keeps the first,
 * so a recording crediting two aliases only offers the first as a seed. That is
 * why the seed is a default the user confirms, never an automatic value —
 * callers must still gate the save on `hasContributionAlias`.
 *
 * Returns '' when nothing matches, which leaves the picker empty and the save
 * blocked until the user chooses.
 */
export const resolveAliasIdByStageName = (
  stageName: string | null | undefined,
  aliases: { stageName: string; personaId: string }[]
): string => {
  const wanted = stageName?.trim().toLowerCase();
  if (!wanted) return '';
  return (
    aliases.find((a) => a.stageName.trim().toLowerCase() === wanted)
      ?.personaId ?? ''
  );
};
